import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Building2,
  Factory,
  FileStack,
  FlaskConical,
  Megaphone,
  TrendingUp,
  Truck,
  Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import { BUNDLES } from "@/data/agreements/bundles";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { ACCENT_CLASSES } from "@/data/agreements/categories";
import { STAGE_BY_ID } from "@/data/agreements/stages";

/**
 * Bundles — route: /agreements/bundles
 *
 * "I am about to do X — what do I need?" answered without filling in the whole intake.
 */

/** bundles.js names its icon by lucide component name; resolve it safely. */
const BUNDLE_ICONS = { Building2, FlaskConical, Factory, Truck, TrendingUp, Users, Megaphone, Briefcase };
const iconFor = (name) => BUNDLE_ICONS[name] || FileStack;

export default function Bundles() {
  return (
    <AgreementsShell
      crumbs={[{ label: "Bundles" }]}
      title="Document bundles"
      subtitle="Each bundle is the set of agreements a founder meets when they take one specific step — hiring a co-packer, signing a distributor, raising a round. Start here when you know what you are about to do."
    >
      <div className="card-grid-auto gap-3">
        {BUNDLES.map((bundle) => {
          const Icon = iconFor(bundle.icon);
          const accent = ACCENT_CLASSES[bundle.accent] || ACCENT_CLASSES.slate;
          const stage = STAGE_BY_ID[bundle.stage];
          const resolved = (bundle.docs || []).filter((d) => DOC_BY_ID[d.id]).length;
          return (
            <Link
              key={bundle.id}
              to={`/agreements/bundles/${bundle.id}`}
              className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full transition-transform group-hover:-translate-y-0.5">
                <CardContent className="flex h-full flex-col gap-3 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={cn(
                        "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1",
                        accent.bg,
                        accent.text,
                        accent.ring,
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {stage && (
                        <Badge variant="outline" className="font-normal">
                          {stage.name}
                        </Badge>
                      )}
                      {bundle.attorneyLed && (
                        <Badge className="gap-1 border-pink-300 bg-pink-100 font-normal text-pink-900 hover:bg-pink-100">
                          <AlertTriangle className="h-3 w-3" /> Attorney-led
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <h2 className="font-semibold leading-tight">{bundle.title}</h2>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{bundle.blurb}</p>
                  </div>

                  <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium">
                    {resolved} document{resolved === 1 ? "" : "s"}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        Not sure which one fits?{" "}
        <Link className="underline" to="/agreements/intake">
          Answer the intake
        </Link>{" "}
        and get a plan built from your own answers instead.
      </p>
    </AgreementsShell>
  );
}
